import { useState, useEffect } from "react";
import { Download } from "lucide-react";
import { exportToExcel, ExcelColumn } from "@/utils/exportUtils";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TablePagination from "@/components/masters/TablePagination";
import { Search, Calendar } from "lucide-react";
import { accountLogApi } from "@/services/api";
import { accountApi } from "@/services/api";

// Type for account log item
interface AccountLogItem {
  id: string;
  transactionNumber: string;
  paymentMethod: string;
  description: string;
  amount: number;
  paymentDate: string;
  doneBy?: { name: string };
  creditAccount?: { id: string; name: string };
  debitAccount?: { id: string; name: string };
  voucher?: { voucherNumber: string } | null;
  collection?: { name: string } | null;
}

interface Account {
  id: string;
  name: string;
  status?: string;
}

const AccountStatementPage = () => {
  const { toast } = useToast();
  const [accountId, setAccountId] = useState("");
  const [type, setType] = useState("all");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statementData, setStatementData] = useState<AccountLogItem[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [allTotals, setAllTotals] = useState<{ credit: number; debit: number }>(
    { credit: 0, debit: 0 }
  );

  const itemsPerPage = 10;

  // Fetch account logs from API
  useEffect(() => {
    // Fetch accounts for dropdowns
    const fetchAccounts = async () => {
      try {
        const response = await accountApi.getAccounts({ status: "true" });
        setAccounts(response.docs || []);
      } catch {
        setAccounts([]);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch logs only when accountId is selected
  useEffect(() => {
    if (!accountId) {
      setStatementData([]);
      setTotalDocs(0);
      return;
    }

    const fetchLogs = async () => {
      setIsLoading(true);
      try {
        const response = await accountLogApi.getAccountLogs({
          page: currentPage,
          limit: itemsPerPage,
          fromDate: dateRange.from || undefined,
          toDate: dateRange.to || undefined,
          accountId: accountId,
          type: type !== "all" && type !== "" ? type : undefined,
        });
        setStatementData(response.docs || []);
        setTotalDocs(response.totalDocs || 0);
      } catch (error) {
        setStatementData([]);
        setTotalDocs(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLogs();
  }, [currentPage, dateRange, accountId, type]);

  useEffect(() => {
    if (!accountId) {
      setAllTotals({ credit: 0, debit: 0 });
      return;
    }
    // Fetch all data for totals
    const fetchAllTotals = async () => {
      try {
        const response = await accountLogApi.getAccountLogs({
          page: 1,
          limit: "all",
          fromDate: dateRange.from || undefined,
          toDate: dateRange.to || undefined,
          accountId: accountId,
          type: type !== "all" && type !== "" ? type : undefined,
        });
        let totalCredit = 0;
        let totalDebit = 0;
        (response.docs || []).forEach((item: AccountLogItem) => {
          const creditAmount =
            item.creditAccount?.id === accountId ||
            (item.creditAccount?.id === accountId &&
              item.debitAccount?.id === accountId)
              ? item.amount
              : 0;
          const debitAmount =
            item.debitAccount?.id === accountId ||
            (item.creditAccount?.id === accountId &&
              item.debitAccount?.id === accountId)
              ? item.amount
              : 0;
          totalCredit += creditAmount || 0;
          totalDebit += debitAmount || 0;
        });
        setAllTotals({ credit: totalCredit, debit: totalDebit });
      } catch {
        setAllTotals({ credit: 0, debit: 0 });
      }
    };
    fetchAllTotals();
  }, [accountId, dateRange, type]);

  const totalPages = Math.ceil(totalDocs / itemsPerPage);

  const handleFilter = () => {
    setCurrentPage(1);
    // Triggers useEffect
  };

  // Function to handle Excel export
  const handleExportToExcel = async () => {
    if (!accountId) {
      toast({
        title: "Account Required",
        description: "Please select an account to export data",
        variant: "destructive",
      });
      return;
    }

    setIsExporting(true);
    try {
      toast({
        title: "Preparing Export",
        description: "Fetching data for export...",
      });

      // Fetch all data for export
      const response = await accountLogApi.getAccountLogs({
        page: 1,
        limit: "all",
        fromDate: dateRange.from || undefined,
        toDate: dateRange.to || undefined,
        accountId: accountId,
        type: type !== "all" && type !== "" ? type : undefined,
      });

      if (!response || !response.docs || response.docs.length === 0) {
        toast({
          title: "No Data",
          description: "No data available to export",
          variant: "destructive",
        });
        setIsExporting(false);
        return;
      }

      // Get selected account name for the filename
      const selectedAccount = accounts.find((acc) => acc.id === accountId);
      const accountName = selectedAccount?.name || "account";

      // Calculate totals
      let totalCredit = 0;
      let totalDebit = 0;

      // Prepare data for export
      const exportData = response.docs.map((item: AccountLogItem) => {
        const creditAmount =
          item.creditAccount?.id === accountId ||
          (item.creditAccount?.id === accountId &&
            item.debitAccount?.id === accountId)
            ? item.amount
            : 0;

        const debitAmount =
          item.debitAccount?.id === accountId ||
          (item.creditAccount?.id === accountId &&
            item.debitAccount?.id === accountId)
            ? item.amount
            : 0;

        // Add to totals
        totalCredit += creditAmount || 0;
        totalDebit += debitAmount || 0;

        return {
          transactionNumber: item.transactionNumber || "-",
          method: item.paymentMethod || "-",
          description: getDescription(item),
          creditAmount: creditAmount ? creditAmount.toLocaleString() : "-",
          debitAmount: debitAmount ? debitAmount.toLocaleString() : "-",
          doneBy: item.doneBy?.name || "-",
          date: formatDate(item.paymentDate),
        };
      });

      // Add a total row
      exportData.push({
        transactionNumber: "",
        method: "",
        description: "TOTAL",
        creditAmount: totalCredit.toLocaleString(),
        debitAmount: totalDebit.toLocaleString(),
        doneBy: "",
        date: "",
      });

      // Define columns for Excel
      const columns: ExcelColumn[] = [
        { header: "Transaction Number", key: "transactionNumber", width: 20 },
        { header: "Method", key: "method", width: 15 },
        { header: "Description", key: "description", width: 30 },
        { header: "Credit Amount", key: "creditAmount", width: 15 },
        { header: "Debit Amount", key: "debitAmount", width: 15 },
        { header: "Done By", key: "doneBy", width: 20 },
        { header: "Date", key: "date", width: 15 },
      ];

      // Generate filename with date
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const filename = `${accountName
        .toLowerCase()
        .replace(/\s+/g, "_")}_statement_${dateStr}.xlsx`;

      // Export to Excel
      await exportToExcel(exportData, {
        filename,
        sheetName: "Account Statement",
        columns,
        styleHeader: true,
      });

      toast({
        title: "Export Successful",
        description: `Account statement exported successfully`,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setAccountId("");
    setType("all");
    setDateRange({ from: "", to: "" });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Helper to format date as dd/mm/yyyy
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  // Helper to get description based on paymentMethod
  const getDescription = (item: AccountLogItem) => {
    if (item.paymentMethod === "receipt" || item.paymentMethod === "payment") {
      return item.voucher?.voucherNumber || "-";
    }
    if (item.paymentMethod === "collection") {
      return item.collection?.name || "-";
    }
    return item.description || "-";
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Account Statement</h1>
        <p className="text-muted-foreground">
          View transaction history and account balance
        </p>
      </div>

      <Card>
        <CardHeader>
          {/* <CardTitle>Report Filters</CardTitle>
          <CardDescription>
            Set parameters to generate account statement
          </CardDescription> */}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="account">Account</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts
                    .filter(
                      (acc) =>
                        typeof acc.id === "string" &&
                        acc.id.trim() !== "" &&
                        acc.name
                    )
                    .map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="receipt">Receipt</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="collection">Collection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="from-date">From Date</Label>
              <div className="flex items-center relative">
                <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="from-date"
                  type="date"
                  className="pl-8"
                  value={dateRange.from}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, from: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="to-date">To Date</Label>
              <div className="flex items-center relative">
                <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="to-date"
                  type="date"
                  className="pl-8"
                  value={dateRange.to}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, to: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={handleReset} disabled={isLoading}>
            Reset
          </Button>
          <Button
            onClick={handleExportToExcel}
            disabled={isLoading || isExporting || !accountId}
            className="whitespace-nowrap"
          >
            <Download className="mr-2 h-4 w-4" />
            Export to Excel
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              {/* <CardTitle>Account Statement</CardTitle>
              <CardDescription>
                {dateRange.from && dateRange.to
                  ? `Period: ${dateRange.from} to ${dateRange.to}`
                  : "All Dates"}
                {creditAccountId !== "" &&
                  ` • Credit Account: ${creditAccountId}`}
                {debitAccountId !== "" && ` • Debit Account: ${debitAccountId}`}
                {type !== "" && ` • Type: ${type}`}
              </CardDescription> */}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transaction Number</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Credit Amount</TableHead>
                <TableHead className="text-right">Debit Amount</TableHead>
                <TableHead>Done By</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="h-24 text-center text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : statementData.length > 0 ? (
                statementData.map((item: AccountLogItem) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.transactionNumber || "-"}</TableCell>
                    <TableCell>{item.paymentMethod || "-"}</TableCell>
                    <TableCell>{getDescription(item)}</TableCell>
                    <TableCell className="text-right">
                      {item.creditAccount?.id === accountId ||
                      (item.creditAccount?.id === accountId &&
                        item.debitAccount?.id === accountId)
                        ? item.amount?.toLocaleString() || "-"
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.debitAccount?.id === accountId ||
                      (item.creditAccount?.id === accountId &&
                        item.debitAccount?.id === accountId)
                        ? item.amount?.toLocaleString() || "-"
                        : "-"}
                    </TableCell>
                    <TableCell>{item.doneBy?.name || "-"}</TableCell>
                    <TableCell>{formatDate(item.paymentDate)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {accountId
                      ? "No transactions found."
                      : "Please select an account to view transactions."}
                  </TableCell>
                </TableRow>
              )}
              {/* Total Row */}
              {!isLoading && statementData.length > 0 && (
                <TableRow className="font-bold bg-muted">
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell className="text-right">
                    {allTotals.credit.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {allTotals.debit.toLocaleString()}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            className="mt-4"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountStatementPage;
